import React from 'react';
import { connect } from 'react-redux';
import { Button } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { mediaPickerActions } from '../store';
import CircleProgressButton from '../../../../buttons/CircleProgressButton';
import { ImageCropperInstance } from './ImageCropper';
import { MEDIA_RESOURCE_TYPE, MEDIA_SOURCE_TYPE, MEDIA_USAGE_TYPE, PARTICLE_SHAPE_TYPES } from '../../CreatePageConstants';
import { createActions } from '../../store';
import { trackEvent, AnalyticsConstants } from '../../../../../utilities/analyticsUtilities';
import { createMediaSourceDataForImage, createMediaSource } from '../../helpers';

const styles = theme => ({
    changeImageButton: {
        marginRight: theme.spacing(2)
    }
});

const ImageCropperActions = (props) => {
    const {
        classes, setProperty, imageCropperProcessing, hideImageCropper, imageCropperData,
        updateVisualizerSettings, updateBackgroundSettings, updateParticleElement, videoSettingsId,
        uploadCroppedImageDirectly
    } = props;

    const onChangeImageClick = () => {
        hideImageCropper();
    };

    const onSaveImageClick = () => {
        if (ImageCropperInstance) {
            setProperty('imageCropperProcessing', true);

            const isParticle = imageCropperData.mediaUsageType === MEDIA_USAGE_TYPE.PARTICLE;
            const resultOptions = isParticle 
                ? { type: 'blob', size: { width: 128, height: 128 }, format: 'png' }
                : { type: 'blob', size: 'original', format: imageCropperData.format };

            ImageCropperInstance.result(resultOptions).then(async function (data) {
                if (isParticle) {
                    setProperty('imageCropperProcessing', false);

                    const reader = new FileReader();
                    reader.onloadend = () => {
                        const particleIndex = imageCropperData.particleIndex;
                        updateParticleElement(particleIndex, {
                            shape: PARTICLE_SHAPE_TYPES.CUSTOM,
                            mediaData: reader.result,
                            color: '0xfbfbfb'
                        });
                        hideImageCropper();
                    };
                    reader.readAsDataURL(data);
                } else {
                    // With Direct Upload the picture goes to storage right away (the spinner
                    // stays on meanwhile) and the preview uses the stored url, so the cropped
                    // picture is not kept in browser memory. With Lazy Upload - or when the
                    // upload fails - it stays local until the project is saved.
                    const uploadedFile = await uploadCroppedImageDirectly(data, imageCropperData.mediaUsageType);
                    setProperty('imageCropperProcessing', false);

                    const activeUrl = uploadedFile ? uploadedFile.url : URL.createObjectURL(data);
                    const mediaData = createMediaSourceDataForImage(activeUrl, imageCropperData.mediaResourceUrl);
                    const mediaSource = createMediaSource(mediaData, imageCropperData.mediaSourceType, imageCropperData.mediaResourceId, MEDIA_RESOURCE_TYPE.IMAGE);
                    if (uploadedFile)
                        mediaSource.directUpload = uploadedFile;

                    switch (imageCropperData.mediaUsageType) {
                        case MEDIA_USAGE_TYPE.LOGO: {
                            updateVisualizerSettings({ mediaSource: mediaSource, logoVisible: true });
                            setProperty('logoMediaPickerOpen', false);
                            break;
                        }
                        case MEDIA_USAGE_TYPE.BACKGROUND: {
                            updateBackgroundSettings({ mediaSource: mediaSource });
                            setProperty('backgroundMediaPickerOpen', false);
                            break;
                        }
                    }

                    trackEvent('Assigned Media', {
                        category: AnalyticsConstants.VIDEO_CATEGORY,
                        type: imageCropperData.mediaUsageType === MEDIA_USAGE_TYPE.LOGO ? 'logo' : 'background',
                        source: imageCropperData.mediaSourceType === MEDIA_SOURCE_TYPE.STOCK_IMAGE ? 'stock' : 'upload',
                        videoId: videoSettingsId
                    });

                    hideImageCropper();
                }
            });
        }
    };

    return (
        <>
            <Button
                className={classes.changeImageButton} 
                onClick={onChangeImageClick}
            >
                Cancel
            </Button>
            <CircleProgressButton
                onClick={onSaveImageClick}
                loading={imageCropperProcessing}
                variant='outlined'
                color='primary'
            >
                Save
            </CircleProgressButton>
        </>
    );
};

const mapStateToProps = (state) => {
    const { 
        imageCropperProcessing, 
        imageCropperData 
    } = state.mediaPicker;

    const { id: videoSettingsId } = state.createPreview.present.videoSettings;

    return { 
        imageCropperProcessing,
        imageCropperData,
        videoSettingsId
    };
}

const mapDispatchToProps = (dispatch) => {
    const setProperty = (key, value) => dispatch(mediaPickerActions.setProperty(key, value));
    const hideImageCropper = () => dispatch(mediaPickerActions.hideImageCropper());
    const updateVisualizerSettings = (properties) => dispatch(createActions.updateVisualizerSettings(properties));
    const updateBackgroundSettings = (properties) => dispatch(createActions.updateBackgroundSettings(properties));
    const updateParticleElement = (index, properties) => dispatch(createActions.updateParticleElement(index, properties));
    const uploadCroppedImageDirectly = (imageBlob, mediaUsageType) => dispatch(createActions.uploadCroppedImageDirectly(imageBlob, mediaUsageType));

    return {
        setProperty,
        hideImageCropper,
        updateVisualizerSettings,
        updateBackgroundSettings,
        updateParticleElement,
        uploadCroppedImageDirectly
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(ImageCropperActions, styles));